// LinearRangeSlider.jsx
import React, { useRef, useState, useEffect } from "react";

function LinearRangeSlider({ range, onRangeChange }) {
  const trackRef = useRef(null);
  const [minVal, setMinVal] = useState(0);
  const [maxVal, setMaxVal] = useState(Object.keys(range).length - 1);

  const keys = Object.keys(range); // ["0-4", "4-6", ..., "+14"]
  const values = Object.values(range);
  const maxIndex = keys.length - 1;

  // Notify parent on change
  useEffect(() => {
    onRangeChange({
      minIdx: minVal,
      maxIdx: maxVal,
      minCol: values[minVal],
      maxCol: values[maxVal],
      minLabel: keys[minVal],
      maxLabel: keys[maxVal],
      isFullyMerged: minVal === maxVal,
    });
  }, [minVal, maxVal]);

  const getValueFromX = (clientX) => {
    const track = trackRef.current;
    if (!track) return 0;
    const rect = track.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    return Math.round(percentage * maxIndex);
  };

  const constrain = (newMin, newMax) => {
    newMin = Math.max(0, Math.min(maxIndex, newMin));
    newMax = Math.max(0, Math.min(maxIndex, newMax));
    if (newMin > newMax) {
      return [newMax, newMin]; // swap if crossed
    }
    return [newMin, newMax];
  };

  const updateRange = (clientX, dragged = null) => {
    const clickedVal = getValueFromX(clientX);
    let newMin = minVal;
    let newMax = maxVal;

    if (dragged === "min") {
      [newMin, newMax] = constrain(clickedVal, maxVal);
    } else if (dragged === "max") {
      [newMin, newMax] = constrain(minVal, clickedVal);
    } else {
      // Clicked on track — move closest handle
      const distToMin = Math.abs(clickedVal - minVal);
      const distToMax = Math.abs(clickedVal - maxVal);
      if (distToMin < distToMax) {
        [newMin, newMax] = constrain(clickedVal, maxVal);
      } else {
        [newMin, newMax] = constrain(minVal, clickedVal);
      }
    }

    setMinVal(newMin);
    setMaxVal(newMax);
  };

  const startDrag = (type) => (e) => {
    e.preventDefault();
    const clientX = e.type.includes("touch") ? e.touches[0].clientX : e.clientX;
    updateRange(clientX, type);
    document.onmousemove = (e) => {
      const x = e.type.includes("touch") ? e.touches[0].clientX : e.clientX;
      updateRange(x, type);
    };
    document.ontouchmove = (e) => {
      e.preventDefault();
      updateRange(e.touches[0].clientX, type);
    };
    document.onmouseup = document.ontouchend = () => {
      document.onmousemove =
        document.ontouchmove =
        document.onmouseup =
        document.ontouchend =
          null;
    };
  };

  const minPercent = (minVal / maxIndex) * 100;
  const maxPercent = (maxVal / maxIndex) * 100;
  const isOverlapped = minVal === maxVal;

  return (
    <div
      className="newUiBody"
      style={{
        // padding: "20px",
        width: "650px",
        height: "150px",
        margin: "10px auto",
        fontFamily: "Arial, sans-serif",
      }}
    >
      <p
        className="m-0 p-0"
        style={{
          textAlign: "center",
          color: "#822a2a",
          fontSize: "14px",
          fontWeight: "bold",
        }}
      >
        Merge Hours Range
      </p>

      {/* Tick Labels */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          margin: "0 15px",
        }}
      >
        {keys.map((k) => (
          <span
            key={k}
            style={{
              fontSize: "11px",
              fontWeight: "bold",
              color: "rgba(169, 50, 50, 1)",
            }}
          >
            {k}
          </span>
        ))}
      </div>

      {/* Slider Track */}
      <div
        ref={trackRef}
        onMouseDown={(e) => updateRange(e.clientX)}
        onTouchStart={(e) => {
          e.preventDefault();
          updateRange(e.touches[0].clientX);
        }}
        style={{
          position: "relative",
          height: "20px",
          background: "#d41919ff",
          borderRadius: "30px",
          margin: "10px 15px",
          cursor: "pointer",
          boxShadow: "inset 0 2px 8px rgba(0,0,0,0.5)",
        }}
      >
        {/* Highlighted Range (disappears when overlapped) */}
        {!isOverlapped && (
          <div
            style={{
              position: "absolute",
              left: `${minPercent}%`,
              width: `${maxPercent - minPercent}%`,
              height: "100%",
              background: "rgba(230, 229, 229, 1)",
              borderRadius: "30px",
              boxShadow: "inset 0 2px 8px rgba(0,0,0,0.5)",
            }}
          />
        )}

        {/* Left Handle */}
        <div
          onMouseDown={startDrag("min")}
          onTouchStart={startDrag("min")}
          style={{
            position: "absolute",
            left: `${minPercent}%`,
            top: "50%",
            transform: "translate(-50%, -50%)",
            width: isOverlapped ? "40px" : "40px",
            height: isOverlapped ? "40px" : "40px",
            background: "#fff",
            border: "2px solid #c92e2eff",
            borderRadius: "50%",
            boxShadow: "0 4px 15px rgba(0,0,0,0.4)",
            cursor: "grab",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: "bold",
            fontSize: isOverlapped ? "12px" : "12px",
            color: "#a93232",
            zIndex: isOverlapped ? 20 : 10,
            transition: "all 0.2s",
          }}
        >
          {isOverlapped ? `All <${keys[minVal]}` : keys[minVal]}
        </div>

        {/* Right Handle (only visible if not overlapped) */}
        {!isOverlapped && (
          <div
            onMouseDown={startDrag("max")}
            onTouchStart={startDrag("max")}
            style={{
              position: "absolute",
              left: `${maxPercent}%`,
              top: "50%",
              transform: "translate(-50%, -50%)",
              width: "40px",
              height: "40px",
              background: "#fff",
              border: "2px solid #c92e2eff",
              borderRadius: "50%",
              boxShadow: "0 4px 15px rgba(0,0,0,0.4)",
              cursor: "grab",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "bold",
              fontSize: "12px",
              color: "#a93232",
              zIndex: 10,
            }}
          >
            {keys[maxVal]}
          </div>
        )}
      </div>

      {/* Info Text */}
      <div
        className="m-0 p-0 "
        style={{
          textAlign: "center",
          marginTop: "2px",
          fontWeight: "bold",
          color: "#333",
        }}
      >
        {isOverlapped ? (
          <span style={{ color: "#a93232" }}>
            All hours merged upto ≤ {keys[minVal]} hrs
          </span>
        ) : (
          <span style={{ fontSize: "12px" }}>
            Showing: <strong>{keys[minVal]}</strong> to{" "}
            <strong>{keys[maxVal]}</strong> hrs
            <br />
            <small style={{ color: "#666" }}>
              Left of {keys[minVal]} and right of {keys[maxVal]} are merged
            </small>
          </span>
        )}
      </div>
    </div>
  );
}

export default LinearRangeSlider;
